import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Shield } from 'lucide-react';

const Chip = ({ ok, children, testId }) => (
  <span data-testid={testId} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${ok ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300'}`}>{children}</span>
);

const fmt = (n) => `RM ${Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}`;

// Read-only summary of an employee's effective statutory contribution settings (defaults + overrides).
export function StatutorySummaryCard({ employee }) {
  const [eff, setEff] = useState(null);
  const [caps, setCaps] = useState({});

  useEffect(() => {
    if (!employee) return;
    axios.get(`${API}/statutory/defaults`, { params: { nationality: employee.nationality || 'malaysian', ic_number: employee.ic_number || undefined } })
      .then((r) => setEff({ ...r.data, ...(employee.statutory_settings || {}) }))
      .catch(() => setEff(employee.statutory_settings || {}));
    axios.get(`${API}/statutory/relief-categories`)
      .then((r) => setCaps(Object.fromEntries((r.data || []).map((c) => [c.code, c.cap]))))
      .catch(() => setCaps({}));
  }, [employee]);

  if (!eff) return null;
  const rows = [
    { label: 'EPF (KWSP)', ok: eff.epf_applicable !== false, detail: eff.epf_applicable === false ? 'Not applicable' : `Employee ${eff.epf_employee_rate ?? 'auto 11'}% · Employer ${eff.epf_employer_rate ?? 'auto 13/12'}%`, id: 'epf' },
    { label: 'SOCSO (PERKESO)', ok: eff.socso_applicable !== false, detail: eff.socso_applicable === false ? 'Not applicable' : (Number(eff.socso_category) === 2 ? 'Category 2 — Employment Injury only (employer)' : 'Category 1 — Injury + Invalidity'), id: 'socso' },
    { label: 'EIS (SIP)', ok: eff.eis_applicable !== false, detail: eff.eis_applicable === false ? 'Not applicable' : '0.2% + 0.2%', id: 'eis' },
    { label: 'PCB / MTD', ok: eff.pcb_applicable !== false, detail: eff.pcb_applicable === false ? 'Not applicable' : 'Computed monthly (LHDN)', id: 'pcb' },
    { label: 'HRDF Levy', ok: !!eff.hrdf_applicable, detail: eff.hrdf_applicable ? '1% employer' : 'Not applicable', id: 'hrdf' },
    { label: 'SKBKK (LINDUNG 24 Jam)', ok: employee.nationality === 'non_malaysian_temporary' || !!eff.skbkk_applicable,
      detail: employee.nationality === 'non_malaysian_temporary'
        ? 'Mandatory (foreign worker) · 0.75% of wages ≤ RM 6,000, employee-borne'
        : eff.skbkk_applicable
          ? ((eff.skbkk_mode || 'auto') === 'manual' ? `Manual · Employee ${fmt(eff.skbkk_employee_amount)} · Employer ${fmt(eff.skbkk_employer_amount)} / month` : 'Enrolled · 0.75% of wages ≤ RM 6,000, employee-borne (auto)')
          : 'Opted out', id: 'skbkk' },
  ];
  const reliefs = employee.tax_reliefs || {};
  const declared = Object.entries(reliefs).filter(([k, v]) => k !== 'zakat_monthly' && Number(v) > 0);
  // Same LHDN caps the payroll engine applies, so the card never overstates the relief.
  const declaredTotal = declared.reduce((s, [k, v]) => s + (caps[k] != null ? Math.min(Number(v || 0), caps[k]) : Number(v || 0)), 0);

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm" data-testid="statutory-summary-card">
      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
        <Shield className="h-5 w-5 text-blue-500" /> Statutory Contributions
      </h3>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-100 dark:border-slate-700 last:border-0">
            <div>
              <p className="text-sm text-slate-800 dark:text-slate-200">{r.label}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400" data-testid={`stat-detail-${r.id}`}>{r.detail}</p>
            </div>
            <Chip ok={r.ok} testId={`stat-chip-${r.id}`}>{r.ok ? 'Yes' : 'No'}</Chip>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-lg bg-slate-50 dark:bg-slate-900 p-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200">LHDN TP1 Tax Reliefs</p>
          <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400" data-testid="tax-relief-declared-total">{fmt(declaredTotal)} / yr</span>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          {declared.length ? `${declared.length} categor${declared.length === 1 ? 'y' : 'ies'} declared${Number(reliefs.zakat_monthly) > 0 ? ` · Zakat ${fmt(reliefs.zakat_monthly)}/month` : ''}` : 'No TP1 declarations — only automatic reliefs (individual, spouse, children, disabled, EPF) apply.'}
        </p>
      </div>
    </div>
  );
}
